import { useEffect, Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { Amplify } from 'aws-amplify';
import { AuthProvider } from './components/auth';
import { AccessibilityProvider } from './contexts/AccessibilityContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { CriticalErrorBoundary, PageErrorBoundary } from './components/error';
import { Layout } from './components/layout/Layout';
import { HomePage } from './pages/HomePage';
import { AuthPage } from './pages/AuthPage';
import { CallbackHandler } from './components/auth/CallbackHandler';
import { ProtectedRoute } from './components/routing/ProtectedRoute';

import { amplifyConfig, isConfigurationComplete } from './config/aws-config';
import { getPerformanceMonitor } from './utils/performanceMonitor';

// Import global styles
import './styles/design-system.css';
import './components/auth/CallbackHandler.css';
import './App.css';

// Performance optimization: Lazy load heavy components
const ChatPage = lazy(() => import('./pages/ChatPage').then(module => ({ default: module.ChatPage })));
const KnowledgeBasePage = lazy(() => import('./pages/KnowledgeBasePage').then(module => ({ default: module.KnowledgeBasePage })));
const SettingsPage = lazy(() => import('./pages/SettingsPage').then(module => ({ default: module.SettingsPage })));
const ConversationHistoryPage = lazy(() => import('./pages/ConversationHistoryPage').then(module => ({ default: module.ConversationHistoryPage })));

// Performance optimization: Loading component for lazy-loaded pages
const PageLoadingFallback = () => (
  <div className="page-loading" style={{ 
    display: 'flex', 
    justifyContent: 'center', 
    alignItems: 'center', 
    height: '200px',
    fontSize: '18px',
    color: '#666'
  }}>
    Loading...
  </div>
);

function App() {
  // Configure Amplify and performance monitoring on app startup
  useEffect(() => {
    try {
      // Performance optimization: Initialize performance monitoring
      const performanceMonitor = getPerformanceMonitor();
      performanceMonitor.recordMetric('app_initialization_start', performance.now());

      Amplify.configure(amplifyConfig);
      
      if (!isConfigurationComplete()) {
        console.warn(
          'AWS configuration is incomplete. Some features may not work properly. ' +
          'Please ensure the CDK stack is deployed and configuration is set up.'
        );
      }

      performanceMonitor.recordMetric('app_initialization_complete', performance.now());
    } catch (error) {
      console.error('Error configuring Amplify:', error);
    }
  }, []);

  return (
    <div className="App">
      <CriticalErrorBoundary>
        <ThemeProvider>
          <AccessibilityProvider>
            <AuthProvider>
            <Router>
              <Layout>
                <Routes>
                  {/* Public Routes */}
                  <Route 
                    path="/" 
                    element={
                      <PageErrorBoundary>
                        <HomePage />
                      </PageErrorBoundary>
                    } 
                  />
                  <Route 
                    path="/auth/*" 
                    element={
                      <PageErrorBoundary>
                        <AuthPage />
                      </PageErrorBoundary>
                    } 
                  />
                  <Route 
                    path="/callback" 
                    element={
                      <PageErrorBoundary>
                        <CallbackHandler />
                      </PageErrorBoundary>
                    } 
                  />
                  
                  {/* Protected Routes with Lazy Loading */}
                  <Route
                    path="/chat"
                    element={
                      <PageErrorBoundary>
                        <ProtectedRoute>
                          <Suspense fallback={<PageLoadingFallback />}>
                            <ChatPage />
                          </Suspense>
                        </ProtectedRoute>
                      </PageErrorBoundary>
                    }
                  />
                  <Route
                    path="/knowledge-base/*"
                    element={
                      <PageErrorBoundary>
                        <ProtectedRoute>
                          <Suspense fallback={<PageLoadingFallback />}>
                            <KnowledgeBasePage />
                          </Suspense>
                        </ProtectedRoute>
                      </PageErrorBoundary>
                    }
                  />
                  <Route
                    path="/settings"
                    element={
                      <PageErrorBoundary>
                        <ProtectedRoute>
                          <Suspense fallback={<PageLoadingFallback />}>
                            <SettingsPage />
                          </Suspense>
                        </ProtectedRoute>
                      </PageErrorBoundary>
                    }
                  />
                  <Route
                    path="/history"
                    element={
                      <PageErrorBoundary>
                        <ProtectedRoute>
                          <Suspense fallback={<PageLoadingFallback />}>
                            <ConversationHistoryPage />
                          </Suspense>
                        </ProtectedRoute>
                      </PageErrorBoundary>
                    }
                  />
                  
                  {/* Catch-all route */}
                  <Route 
                    path="*" 
                    element={
                      <PageErrorBoundary>
                        <HomePage />
                      </PageErrorBoundary>
                    } 
                  />
                </Routes>
              </Layout>

            </Router>
            </AuthProvider>
          </AccessibilityProvider>
        </ThemeProvider>
      </CriticalErrorBoundary>
    </div>
  );
}

export default App;